<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { Star, Inbox, Settings2 } from 'lucide-vue-next'
import type { DeliveryReview, ReviewStatsSummary } from '@/types/reviewPlatforms'
import ReviewPlatformItem from '../reviews/ReviewPlatformItem.vue'
import ReviewPlatformStats from '../reviews/ReviewPlatformStats.vue'

const emit = defineEmits<{
  'go-to-config': []
}>()

const { t } = useI18n()
const { stores } = useLocalStorage()

const isLoaded = ref(false)
const fetchError = ref(false)
const reviews = ref<DeliveryReview[]>([])
const totalPages = ref(1)
const currentPage = ref(1)
const totalReviews = ref(0)

const selectedStoreId = ref<number | null>(null)
const selectedPlatformSlugs = ref<('deliveroo' | 'ubereats')[]>(['deliveroo', 'ubereats'])
const selectedNotes = ref<number[]>([])
const onlyUnreplied = ref(false)

const PAGE_SIZE = 10
const DEFAULT_DATE_RANGE_DAYS = 30
const dateValue = ref<string[]>([
  dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'day').format('DD-MM-YYYY'),
  dayjs().format('DD-MM-YYYY')
])

const stats = ref<ReviewStatsSummary>({
  total: 0,
  averageRating: 0,
  unrepliedCount: 0,
  negativeCount: 0
})

const platformOptions = [
  { slug: 'deliveroo' as const, label: 'Deliveroo' },
  { slug: 'ubereats' as const, label: 'Uber Eats' }
]

const effectiveSlugs = computed(() =>
  selectedPlatformSlugs.value.length > 0 ? selectedPlatformSlugs.value : platformOptions.map((p) => p.slug)
)

const activeFiltersCount = computed(() => {
  let count = 0
  if (selectedPlatformSlugs.value.length < platformOptions.length) count++
  if (selectedNotes.value.length > 0) count++
  if (onlyUnreplied.value) count++
  return count
})

async function fetchReviews(page = 1) {
  isLoaded.value = false
  fetchError.value = false
  try {
    const params: Record<string, unknown> = {
      platformSlugs: effectiveSlugs.value.join(','),
      page,
      pageSize: PAGE_SIZE
    }
    if (selectedStoreId.value !== null) {
      params.storeIds = selectedStoreId.value
    }
    if (selectedNotes.value.length > 0) {
      params.notes = selectedNotes.value.join(',')
    }
    if (Array.isArray(dateValue.value) && dateValue.value.length === 2) {
      params.fromDate = dateValue.value[0]
      params.toDate = dateValue.value[1]
    }
    if (onlyUnreplied.value) {
      params.hasAnswered = false
    }

    const { data } = await axios.get('/reviews', { params })
    reviews.value = data.data
    currentPage.value = data.meta?.current_page ?? page
    totalPages.value = data.meta?.last_page ?? 1
    totalReviews.value = data.meta?.total ?? 0
  } catch {
    fetchError.value = true
  } finally {
    isLoaded.value = true
  }
}

async function fetchStats() {
  try {
    const params: Record<string, unknown> = {
      platformSlugs: effectiveSlugs.value.join(',')
    }
    if (selectedStoreId.value !== null) {
      params.storeIds = selectedStoreId.value
    }
    if (Array.isArray(dateValue.value) && dateValue.value.length === 2) {
      params.fromDate = dateValue.value[0]
      params.toDate = dateValue.value[1]
    }
    const { data } = await axios.get('/reviews/stats', { params })
    const apiStats = data.data ?? {}
    stats.value = {
      total: apiStats.total ?? totalReviews.value,
      averageRating: apiStats.average ?? 0,
      unrepliedCount: apiStats.unreplied_count ?? 0,
      negativeCount: apiStats.negative_count ?? 0
    }
  } catch {
    // stats failure is non-blocking, reviews list still shows
  }
}

async function applyFilters() {
  await Promise.all([fetchReviews(1), fetchStats()])
}

function onStoreSelected(storeId: number) {
  selectedStoreId.value = storeId || null
  applyFilters()
}

function onDateRangeChanged(val: string[]) {
  dateValue.value = val
  applyFilters()
}

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  fetchReviews(page)
}

function onReviewReplied(reviewId: number, responseText: string, compensationAmount: number | null) {
  const idx = reviews.value.findIndex((r) => r.id === reviewId)
  if (idx !== -1) {
    reviews.value[idx] = { ...reviews.value[idx], ownerResponseText: responseText, compensationAmount }
  }
  fetchStats()
}

const showEmptyState = computed(() => isLoaded.value && !fetchError.value && reviews.value.length === 0)
const emptyStateTitle = computed(() =>
  activeFiltersCount.value > 0
    ? t('pages.reviewPlatforms.reviews.empty.filteredTitle')
    : t('pages.reviewPlatforms.reviews.empty.title')
)
const emptyStateDescription = computed(() =>
  activeFiltersCount.value > 0
    ? t('pages.reviewPlatforms.reviews.empty.filteredDescription')
    : t('pages.reviewPlatforms.reviews.empty.description')
)

onMounted(async () => {
  await Promise.all([fetchReviews(1), fetchStats()])
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="text-xs text-muted-foreground">
        {{ t('pages.reviewPlatforms.reviews.autoRefreshInfo') }}
      </p>
      <ThePageSettings
        :date-range-value="dateValue"
        :display-store-selector="stores.length > 1"
        display-date-range-picker
        display-filters
        @store-selected="onStoreSelected"
        @update:date-range-value="onDateRangeChanged"
        @validate-filters="applyFilters"
      >
        <template #filtersTitle>
          {{ t('pages.reviewPlatforms.reviews.filters.title') }}
        </template>
        <template #filtersDescription>
          {{ t('pages.reviewPlatforms.reviews.filters.description') }}
        </template>
        <template #filtersContent>
          <div class="space-y-6">
            <fieldset>
              <legend class="text-sm font-semibold text-foreground">
                {{ t('pages.reviewPlatforms.reviews.filters.platform') }}
              </legend>
              <div class="mt-3 space-y-2">
                <AppCheckboxField
                  v-for="platform in platformOptions"
                  :id="`platform-${platform.slug}`"
                  :key="platform.slug"
                  v-model="selectedPlatformSlugs"
                  :value="platform.slug"
                  :label="platform.label"
                />
              </div>
            </fieldset>

            <fieldset>
              <legend class="text-sm font-semibold text-foreground">
                {{ t('pages.reviewPlatforms.reviews.filters.note') }}
              </legend>
              <div class="mt-3 space-y-2">
                <AppCheckboxField
                  v-for="note in 5"
                  :id="`note-${note}`"
                  :key="note"
                  v-model="selectedNotes"
                  :value="note"
                >
                  <template #label>
                    <div class="flex items-center gap-0.5">
                      <Star v-for="i in note" :key="i" class="size-4 fill-amber-400 text-amber-400" />
                    </div>
                  </template>
                </AppCheckboxField>
              </div>
            </fieldset>

            <fieldset>
              <legend class="text-sm font-semibold text-foreground">
                {{ t('pages.reviewPlatforms.reviews.filters.status') }}
              </legend>
              <div class="mt-3">
                <AppCheckboxField
                  id="only-unreplied"
                  v-model="onlyUnreplied"
                  :label="t('pages.reviewPlatforms.reviews.filters.onlyUnreplied')"
                />
              </div>
            </fieldset>
          </div>
        </template>
      </ThePageSettings>
    </div>

    <ReviewPlatformStats :stats="stats" :is-loading="!isLoaded && reviews.length === 0" />

    <div v-if="!isLoaded" class="flex flex-col gap-3">
      <Skeleton v-for="i in 4" :key="i" class="h-32 w-full rounded-xl" />
    </div>

    <Alert v-else-if="fetchError" variant="destructive">
      <AlertDescription>{{ t('pages.reviewPlatforms.reviews.fetchError') }}</AlertDescription>
    </Alert>

    <template v-else>
      <div
        v-if="showEmptyState"
        class="flex flex-col items-center gap-4 rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center"
      >
        <div class="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Inbox class="size-6" />
        </div>
        <div class="space-y-1">
          <p class="text-base font-semibold text-foreground">
            {{ emptyStateTitle }}
          </p>
          <p class="max-w-md text-sm text-muted-foreground">
            {{ emptyStateDescription }}
          </p>
        </div>
        <Button v-if="activeFiltersCount === 0" variant="default-primary" size="sm" @click="emit('go-to-config')">
          <Settings2 />
          {{ t('pages.reviewPlatforms.reviews.empty.cta') }}
        </Button>
      </div>

      <div v-else class="flex flex-col gap-3">
        <ReviewPlatformItem v-for="review in reviews" :key="review.id" :review="review" @replied="onReviewReplied" />
      </div>

      <BasePagination
        v-if="!showEmptyState && totalPages > 1"
        :current-page="currentPage"
        :last-page="totalPages"
        :total="totalReviews"
        :page-size="PAGE_SIZE"
        @update:change-page="goToPage"
      />
    </template>
  </div>
</template>
